import { Moment } from "moment";
import { Page, QueryBase } from "../../../shared/models/models";

export enum ServiceRequestSortParameter {
  internalNumber = 1,
  openDate = 2,
  createdDate = 3,
}

export enum ServiceRequestStatusCode {
  draft = 1,
  openForOffers = 2,
  negotiating = 3,
  inProgress = 4,
  completed = 5,
  archived = 6,
  awaitingProviderConfirmation = 7,
  awaitingCompletionConfirmation = 8,
}

export interface ServiceRequestsQuery extends QueryBase<ServiceRequestSortParameter> {
  statusCodes: ServiceRequestStatusCode[];
  dateFrom?: string;
  dateTo?: string;
}

export enum ServiceTypeCode {
  onSiteRepair = 1,
  maintenance = 2,
  partsReplacement = 3,
}

export enum PriorityTypeCode {
  urgent = 1,
  high = 2,
  low = 4,
}

export enum BudgetTypeCode {
  fixed = 1,
  range = 2,
  requestQuote = 3,
}

export enum PaymentTermsCode {
  net30Days = 1,
  net60Days = 2,
  net90Days = 3,
  dueOnReceipt = 4,
  upfront50Completion = 5,
}

export interface ServiceRequestMachine {
  id: string;
  name: string;
  brandName?: string;
  imageUrl?: string;
  catalogImageUrl?: string;
};

export interface ServiceRequestFile {
  name: string;
  content: string;
  size: number;
  typeCode?: number;
};

export interface ServiceRequestBase {
  issue: string;
  description: string;
  affectedPartCategoryId: string;
  openDate: Date;
  serviceTypeCode: ServiceTypeCode;
  priorityTypeCode: PriorityTypeCode;
  flexibleDates: boolean;
  serviceStartDate?: Date;
  serviceEndDate?: Date;
  worksiteAccessEnabled: boolean;
  workingHoursFrom?: string;
  workingHoursTo?: string;
  weekendWorkPossible?: boolean;
  afterHourAccessAvailable?: boolean;
  budgetEnabled: boolean;
  budgetTypeCode?: BudgetTypeCode;
  fixedBudget?: number;
  budgetRangeFrom?: number;
  budgetRangeTo?: number;
  paymentTermsEnabled: boolean;
  paymentTermsCode?: PaymentTermsCode;
  notes?: string;
  alarmCode?: string;
  locationId?: string;
  files?: ServiceRequestFile[];
}

export interface AcceptedOfferSummary {
  offerId: string;
  providerName: string;
  providerLogoUrl?: string;
  providerId: string;
  providerAverageRating?: number;
  providerReviewCount?: number;
  confirmedArrivalDate?: string;
  confirmedArrivalTimeFrom?: string;
  confirmedArrivalTimeTo?: string;
  totalEstimatedCost?: number;
  reviewRating?: number;
  reviewTitle?: string;
  reviewComment?: string;
  reviewDate?: string;
  statusCode: number;
  workCompletionNote?: string;
  workCompletedDate?: string;
  files?: Array<{ name: string; size: number }>;
  workCompletionFiles?: Array<{ name: string; size: number }>;
  // OC-Phase2: Additional fields for context panel enrichment
  acceptedDate?: string;
  confirmedByProviderDate?: string;
  confirmationDeadline?: string;
  confirmationMessage?: string;
  earliestStartDate?: string;
  estimatedDuration?: string;
  completionTimeCode?: number;
  serviceApproach?: string;
  // Logbook enrichment: Provider's message to customer (from offer creation)
  messageToCustomer?: string;
  // OC-Phase3: New columns + computed data
  workStartedDate?: string;
  progressUpdateCount?: number;
  lastProgressUpdateDate?: string;
  liveTrackingActive?: boolean;
  actualLaborHours?: number;
  actualPartsCost?: number;
  actualTotalCost?: number;
  ownerAcceptedDate?: string;
  revisionRequested?: boolean;
  revisionCount?: number;
  statusChangedDate?: string;
}

export interface ServiceRequest extends ServiceRequestBase {
  id: string;
  machine: ServiceRequestMachine;
  serviceReportNumber?: string;
  internalNumber: string;
  statusCode: ServiceRequestStatusCode;
  visibilityCode?: number;
  targetPartnerId?: string;
  targetPartnerName?: string;
  locationName?: string;
  parentLocationName?: string;
  createdDate: Date;
  modifiedDate: Date;
  offersCount?: number;
  newOffersCount?: number;
  acceptedOffer?: AcceptedOfferSummary;
  lowestOfferPrice?: number;
}

export interface ServiceRequestStats {
  totalRequests: number;
  requestsWithNewOffers: number;
}

export interface ServiceRequestsState {
  serviceRequestsQuery: ServiceRequestsQuery;
  serviceRequests?: Page<ServiceRequest>;
  serviceRequestsFilters?: ServiceRequestsFilters;
  serviceRequest?: ServiceRequest;
  serviceRequestStats?: ServiceRequestStats;
  loading: boolean;
  success?: string;
  error: any;
}

export interface CreateServiceRequest extends ServiceRequestBase {
  machineId: string;
  serviceReportNumber?: string;
  visibilityCode?: number;
  targetPartnerId?: string;
  targetPartnerIds?: string[];
}

export interface ChangeServiceRequest extends ServiceRequestBase {
  visibilityCode?: number;
  targetPartnerId?: string;
  targetPartnerIds?: string[];
}

export interface ServiceRequestFormData extends Omit<
  ServiceRequestBase,
  'serviceTypeCode' | 'priorityTypeCode' | 'workingHoursFrom' | 'workingHoursTo'
> {
  serviceTypeCode?: ServiceTypeCode;
  priorityTypeCode?: PriorityTypeCode;
  workingHoursFrom?: Moment;
  workingHoursTo?: Moment;
  locationId?: string;
}

export interface FilterParameter<TKey> {
  id: TKey;
  name: string;
  value: number;
}

export interface ServiceRequestsFiltersQuery {
  search?: string;
}

export interface ServiceRequestsFilters {
  statusCodes: FilterParameter<ServiceRequestStatusCode>[];
}